import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button } from '@mui/material';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  /** When provided, buttons are disabled while true and the dialog prevents close. Caller controls when to close. */
  loading?: boolean;
  /** Hides the confirm button, leaving an informational dialog the user can only dismiss. */
  hideConfirm?: boolean;
  /** Optional content rendered inside DialogContent after the message text. */
  children?: React.ReactNode;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  destructive = false,
  loading,
  hideConfirm = false,
  children,
}) => {
  const handleConfirm = () => {
    onConfirm();
    // Auto-close only when the caller isn't managing async loading state
    if (loading === undefined) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          {cancelText}
        </Button>
        {!hideConfirm && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            disabled={loading}
            autoFocus
          >
            {confirmText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
